import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import AnimatedBackground from "@/components/site/AnimatedBackground";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { ArrowRight } from "lucide-react";

const FIELD =
  "w-full border-b border-white/20 bg-transparent py-3 text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-brand-blue";

export default function Contact() {
  const { content } = useContent();
  const C = content.contact;
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "General Enquiry",
    course: params.get("course") || "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Enquiry sent!");
      setForm({ name: "", email: "", subject: "General Enquiry", course: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="relative pt-32">
      <AnimatedBackground variant="default" />
      <section className="mx-auto grid max-w-[1400px] gap-16 px-6 pb-28 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <p className="font-mono text-sm uppercase tracking-[0.3em] text-brand-blue">{C.label}</p>
          <h1 className="mt-6 font-display text-5xl font-medium leading-[0.95] tracking-tight text-white sm:text-6xl">
            <MaskedLines lines={[C.headingLine1, C.headingLine2]} />
            <MaskedLines lines={[C.headingLine3]} delay={0.2} lineClassName="text-brand-gradient" />
          </h1>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-sm text-lg font-light leading-relaxed text-slate-400">
              {C.blurb}
            </p>
            <div className="mt-10 space-y-4 font-mono text-base text-slate-300">
              <p><span className="text-slate-500">Email — </span>{C.email}</p>
              <p><span className="text-slate-500">Where — </span>{C.location}</p>
              <p><span className="text-slate-500">Response — </span>{C.response}</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
            <div>
              <label className="font-mono text-sm uppercase tracking-[0.2em] text-slate-500">Name</label>
              <input data-testid="contact-name" className={FIELD} value={form.name} onChange={set("name")} placeholder="Your full name" />
            </div>
            <div>
              <label className="font-mono text-sm uppercase tracking-[0.2em] text-slate-500">Email</label>
              <input data-testid="contact-email" type="email" className={FIELD} value={form.email} onChange={set("email")} placeholder="you@company.com" />
            </div>
            <div className="grid gap-8 sm:grid-cols-2">
              <div>
                <label className="font-mono text-sm uppercase tracking-[0.2em] text-slate-500">Subject</label>
                <input data-testid="contact-subject" className={FIELD} value={form.subject} onChange={set("subject")} placeholder="General enquiry" />
              </div>
              <div>
                <label className="font-mono text-sm uppercase tracking-[0.2em] text-slate-500">Course (optional)</label>
                <input data-testid="contact-course" className={FIELD} value={form.course} onChange={set("course")} placeholder="e.g. SAS Clinical" />
              </div>
            </div>
            <div>
              <label className="font-mono text-sm uppercase tracking-[0.2em] text-slate-500">Message</label>
              <textarea data-testid="contact-message" rows={4} className={`${FIELD} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell me a little about your goals…" />
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="brand-glow-blue group flex items-center gap-3 bg-brand-blue px-8 py-4 font-mono text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-magenta disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send enquiry"}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
